import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Compass, KeyRound } from 'lucide-react';

// Catch-all 404 page. Without this React Router renders nothing inside
// <main>, leaving Navbar + Footer + a big white gap — which has been
// confused with a "broken page" by users (notably when password-reset
// links arrive mangled by Microsoft 365 Safe Links and land on a path
// that doesn't match any of our routes). This page also offers a
// shortcut to request a fresh reset link, since that's the most
// common reason real users end up here.
export default function NotFoundPage() {
  const { pathname } = useLocation();
  // Best-effort: if the user landed on something that *looks* like a
  // reset-password URL (e.g. mangled query-string from an email
  // client), nudge them straight to the forgot-password flow.
  const looksLikeResetUrl =
    pathname.toLowerCase().includes('reset-password') ||
    pathname.toLowerCase().includes('reset_password') ||
    pathname.toLowerCase().includes('resetpassword');

  return (
    <div data-testid="not-found-page" className="min-h-[60vh] flex items-center justify-center py-12 px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-[#56c6df]/10 flex items-center justify-center mx-auto mb-4">
          <Compass className="w-8 h-8 text-[#56c6df]" />
        </div>
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Page not found</h1>
        <p className="text-slate-500 mb-2">We couldn't find the page you were looking for.</p>
        {looksLikeResetUrl && (
          <p className="text-slate-500 mb-2">If you arrived here from a password-reset email, the link may have been mangled by your email provider.</p>
        )}
        <p className="text-xs text-slate-400 mb-8">Path: <code className="bg-slate-100 px-2 py-0.5 rounded">{pathname}</code></p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/">
            <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8" data-testid="not-found-home-btn">
              Back to Home
            </Button>
          </Link>
          {looksLikeResetUrl && (
            <Link to="/forgot-password">
              <Button variant="outline" className="rounded-full px-6 gap-2" data-testid="not-found-reset-btn">
                <KeyRound className="w-4 h-4" /> Request Password Reset
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
